import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { env } from './env';

/**
 * Initialize Datadog RUM (Real User Monitoring)
 * Captures user console logs, errors, and performance metrics
 *
 * Setup instructions:
 * 1. Go to https://app.datadoghq.com/rum/application/create
 * 2. Create a new RUM application
 * 3. Copy the Application ID, Client Token, and Site
 * 4. Add them to your .env.local file
 */
export function initDatadog() {
  if (
    !env.NEXT_PUBLIC_DATADOG_APP_ID ||
    !env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ||
    !env.NEXT_PUBLIC_DATADOG_SITE
  ) {
    console.log('[Datadog] Skipping initialization - credentials not provided');
    return;
  }

  // Already initialized (e.g. React StrictMode double-invoking effects)
  if (datadogRum.getInitConfiguration()) {
    return;
  }

  const proxy:
    | ((opts: { path: string; parameters: string }) => string)
    | undefined = env.NEXT_PUBLIC_DATADOG_RUM_PROXY
    ? (opts) =>
        `${env.NEXT_PUBLIC_DATADOG_RUM_PROXY}/t${opts.path}?${opts.parameters}`
    : undefined;

  try {
    // Initialize RUM
    datadogRum.init({
      applicationId: env.NEXT_PUBLIC_DATADOG_APP_ID,
      clientToken: env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      site: env.NEXT_PUBLIC_DATADOG_SITE,
      service:
        env.NEXT_PUBLIC_DATADOG_APPLICATION_RUM_NAME || 'creatr-frontend',
      env: env.NEXT_PUBLIC_NODE_ENV || 'development',
      proxy,
      version: env.NEXT_PUBLIC_APP_VERSION,
      trackResources: true,
      trackUserInteractions: true,
      trackLongTasks: true,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      defaultPrivacyLevel: 'allow',

      allowedTracingUrls: [
        {
          match: env.NEXT_PUBLIC_API_URL || 'http://localhost:3005',
          propagatorTypes: ['tracecontext', 'datadog'],
        },
      ],
      traceSampleRate: 100,

      // Filter out Next.js internal pseudo-errors (redirect(), notFound(), etc.)
      beforeSend: (event) => {
        if (event.type === 'error') {
          const msg = event.error.message ?? '';
          if (
            msg.includes('NEXT_REDIRECT') ||
            msg.includes('NEXT_NOT_FOUND') ||
            msg.includes('NEXT_HTTP_ERROR_FALLBACK')
          ) {
            return false;
          }
        }
        return true;
      },
    });

    datadogRum.startSessionReplayRecording();

    // Initialize Logs for console log forwarding (production only)
    datadogLogs.init({
      clientToken: env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      site: env.NEXT_PUBLIC_DATADOG_SITE,
      service:
        env.NEXT_PUBLIC_DATADOG_APPLICATION_RUM_NAME || 'creatr-frontend',
      env: env.NEXT_PUBLIC_NODE_ENV || 'development',
      proxy,
      version: env.NEXT_PUBLIC_APP_VERSION,

      // Forward console logs to Datadog
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['error', 'warn', 'info'], // Choose which log levels to forward
      forwardReports: ['intervention', 'deprecation', 'csp_violation'], // Browser reports

      // Session tracking (links logs to RUM sessions)
      sessionSampleRate: 100,
      beforeSend: (log) => {
        const rumContext = datadogRum.getInternalContext();
        if (rumContext?.session_id) {
          (log as Record<string, unknown>).rum_session_id =
            rumContext.session_id;
        }
        return true;
      },
    });
    console.log('[Datadog] RUM and Logs initialized successfully');
  } catch (error) {
    console.error('[Datadog] Failed to initialize:', error);
  }
}

/**
 * Set user context for Datadog RUM
 * Call this after user logs in to associate sessions with users
 */
export function setDatadogUser(user: {
  id: string;
  email?: string;
  name?: string;
}) {
  try {
    datadogRum.setUser({
      id: user.id,
      email: user.email,
      name: user.name,
    });
    // datadogLogs is a separate SDK — user context is not shared automatically
    datadogLogs.setUser({
      id: user.id,
      email: user.email,
      name: user.name,
    });
  } catch (error) {
    console.error('[Datadog] Failed to set user:', error);
  }
}

/**
 * Clear user context (e.g., on logout)
 */
export function clearDatadogUser() {
  try {
    datadogRum.clearUser();
    datadogLogs.clearUser();
  } catch (error) {
    console.error('[Datadog] Failed to clear user:', error);
  }
}

/**
 * Add custom action tracking
 */
export function trackDatadogAction(
  name: string,
  context?: Record<string, unknown>
) {
  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    console.error('[Datadog] Failed to track action:', error);
  }
}

/**
 * Manually capture an error with context
 */
export function captureDatadogError(
  error: Error,
  context?: Record<string, unknown>
) {
  try {
    datadogRum.addError(error, context);
  } catch (error) {
    console.error('[Datadog] Failed to capture error:', error);
  }
}
